// ============================================================================
// USE DOCUMENTS WORK ITEMS HOOK
// Unified hook for finance documents using work_items backend
// Provides same interface as useDocuments for backward compatibility
// Date: December 3, 2025
// ============================================================================

'use client';

import { useState, useCallback } from 'react';
import { authService } from '@/lib/authService';

// ============================================================================
// TYPES
// ============================================================================

export type DocumentType =
  // Sales
  | 'invoice'
  | 'quote'
  | 'proposal'
  | 'quote_request'
  | 'billing_submission'
  | 'credit_note'
  | 'debit_note'
  | 'proforma'
  | 'delivery_note'
  | 'waybill'
  | 'sales_order'
  | 'rma'
  // Purchases
  | 'purchase_order'
  | 'grn'
  | 'supplier_credit'
  | 'supplier_quote'
  | 'purchase_requisition'
  | 'rfq'
  | 'purchase_return'
  | 'purchase'
  | 'supplier_invoice'
  | 'supplier_delivery_note'
  // Operations
  | 'work_order'
  // Expenses (unified 'expense' with item_subtype)
  | 'expense'
  // Document Intake (work item type, not a finance document type)
  | 'din'
  // Warehouse
  | 'picking_slip'
  | 'packing_slip'
  | 'stock_adjustment'
  | 'transfer_order'
  // Property Finance
  | 'levy_cycle'
  | 'meter_reading'
  | 'utility_recovery'
  | 'special_levy'
  | 'scheme_budget'
  | 'reserve_fund'
  | 'deposit_holding'
  | 'trust_transaction'
  | 'payment_dispute'
  | 'payment_plan'
  | 'collection_case'
  | 'invoice_followup'
  | 'credit_request'
  | 'write_off';

const WORK_ITEM_DOCUMENT_TYPES = [
  'invoice',
  'quote',
  'proposal',
  'billing_submission',
  'credit_note',
  'debit_note',
  'proforma',
  'delivery_note',
  'waybill',
  'sales_order',
  'rma',
  'purchase_order',
  'grn',
  'supplier_credit',
  'supplier_quote',
  'purchase_requisition',
  'rfq',
  'purchase_return',
  'purchase',
  'supplier_invoice',
  'supplier_delivery_note',
  'work_order',
  'expense',
  'picking_slip',
  'packing_slip',
  'stock_adjustment',
  'transfer_order',
  'levy_cycle',
  'meter_reading',
  'utility_recovery',
  'special_levy',
  'scheme_budget',
  'reserve_fund',
  'deposit_holding',
  'trust_transaction',
  'payment_dispute',
  'payment_plan',
  'collection_case',
  'invoice_followup',
  'credit_request',
  'write_off',
] as const satisfies readonly DocumentType[];

const WORK_ITEM_DOCUMENT_TYPE_SET = new Set<DocumentType>(WORK_ITEM_DOCUMENT_TYPES);

function normalizeWorkItemDocumentTypes(
  docType?: DocumentType | DocumentType[]
): DocumentType[] | undefined {
  if (!docType) {
    return undefined;
  }

  const values = Array.isArray(docType) ? docType : [docType];
  const normalized = values.filter((value): value is DocumentType => WORK_ITEM_DOCUMENT_TYPE_SET.has(value));

  return normalized.length > 0 ? Array.from(new Set(normalized)) : [];
}

function appendQueryParam(
  params: URLSearchParams,
  key: string,
  value: string | number | boolean | Array<string | number | boolean> | undefined | null,
) {
  if (value === undefined || value === null) {
    return;
  }

  if (Array.isArray(value)) {
    for (const entry of value) {
      params.append(key, String(entry));
    }
    return;
  }

  params.set(key, String(value));
}

export type DocumentStatus =
  | 'draft'
  | 'pending_approval'
  | 'submitted_to_finance'
  | 'approved_for_conversion'
  | 'returned_to_operations'
  | 'sent'
  | 'viewed'
  | 'accepted'
  | 'declined'
  | 'approved'
  | 'under_review'
  | 'partial'
  | 'paid'
  | 'overdue'
  | 'cancelled'
  | 'void'
  | 'expired'
  | 'converted'
  | 'received'
  | 'partially_received'
  | 'closed'
  | 'fulfilled'
  | 'shipped'
  | 'delivered'
  // Work-item-only statuses (DINs and similar intake documents)
  | 'new'
  | 'ready'
  | 'to_review'
  | 'completed'
  | 'failed';

export type PaymentStatus = 'unpaid' | 'partial' | 'paid' | 'overdue' | 'refunded' | 'cancelled';

export type LineItemType = 'item' | 'expense_line' | 'service' | 'expense' | 'header' | 'comment' | 'subtotal' | 'spacer' | 'tip';

export interface DocumentLineItem {
  id?: number;
  document_id?: string;
  item_id?: number | string;  // Can be number or string from different sources
  item_code?: string;
  item_name?: string;
  description: string;
  notes?: string;
  quantity: number;
  unit?: string;
  unit_of_measure?: string;   // Alternative field for unit
  unit_price: number;
  rate?: number;
  unit_cost?: number;         // Cost price (for purchases)
  discount_amount?: number;
  discount_percentage?: number;
  line_total?: number;
  amount?: number;
  total_cost?: number;
  tax_rate_id?: number;
  tax_rate: number;
  tax_inclusive?: boolean;  // When true, unit_price includes tax (VAT-inclusive)
  job_number?: string | null;  // Job number for this line item
  appointment_number?: string | null;  // Appointment number for this line item
  tax_amount: number;
  sort_order?: number;
  category_id?: number | null;
  category_name?: string | null;
  quantity_ordered?: number;
  quantity_received?: number;
  quantity_billed?: number;
  quantity_shipped?: number;
  line_type?: LineItemType;  // Type of line: item, header, comment, subtotal, spacer
  is_optional?: boolean;
  is_required?: boolean;
  quantity_editable?: boolean;
  price_editable?: boolean;
  source_bundle_id?: string;
  source_bundle_name?: string;

  // Source tracking for job-to-invoice linking
  source_work_item_id?: string;
  source_time_entry_id?: string;

  // Purchase sourcing metadata (persisted in BOM note metadata)
  purchase_supplier_mapping_id?: number;
  purchase_supplier_contact_id?: number;
  purchase_supplier_name?: string | null;
  purchase_supplier_sku?: string | null;
  purchase_supplier_currency?: string | null;
  purchase_supplier_cost_price?: number | null;
  purchase_supplier_is_preferred?: boolean;
  purchase_supplier_priority_rank?: number | null;
  purchase_supplier_lead_time_days?: number | null;
  purchase_supplier_selection_reason?: string | null;
  purchase_supplier_fallback_reason?: string | null;
  purchase_supplier_price_source?: string | null;
  purchase_supplier_matches_document_contact?: boolean;

  // Line item comments
  billing_comments?: string;

  // Expense GL account allocation
  expense_account_code?: string;
  account_code?: string;
  allocation_type?: 'job' | 'stock' | 'overhead' | 'equipment' | 'vehicle' | 'suspense';
  markup_percentage?: number;
  linked_equipment_item_id?: string;
  linked_equipment_name?: string;
  linked_vehicle_id?: string;
  linked_vehicle_name?: string;
  linked_job_id?: string;
  linked_job_name?: string;
  linked_customer_id?: string;
  linked_customer_name?: string;
  linked_inventory_item_id?: string;
  linked_inventory_name?: string;
  linked_inventory_sku?: string;
  is_billable?: boolean;
  billable_to_job_id?: string;

  // Split parentage tracking (set when a line item is created via splitting)
  split_parent_ref?: string;

  // Multi-dimensional and split allocations (JSONB on work_item_bom_items)
  allocations?: {
    dimensions?: Array<{ dimension_key: string; dimension_value: string; percentage: number }>;
    splits?: Array<{ id: string; gl_account_code: string; percentage: number; description?: string }>;
  } | null;

  // Inventory tracking flag (copied from FinanceItem at selection time)
  track_inventory?: boolean;
}

export interface Document {
  id: string;
  client_id?: number;
  doc_type: DocumentType;
  item_type?: DocumentType;
  doc_number: string;
  item_number?: string;  // Alternative document number field
  title?: string;
  description?: string;
  status: DocumentStatus;

  // Dates
  issued_at: string;
  due_date?: string;
  expiry_date?: string;
  valid_until?: string;     // Quote validity date
  delivery_date?: string;   // Expected delivery/fulfillment date
  transaction_date?: string; // Transaction date (for DIN/EXP/supplier_invoice — the date on the receipt/invoice, distinct from the intake date)

  // Contact
  contact_id?: number;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  billing_address?: string;
  shipping_address?: string;

  // Location (for service/delivery)
  location_id?: number;
  location_name?: string;
  location_address?: string;

  // Warehouse (for inventory operations)
  warehouse_id?: number;
  warehouse_name?: string;

  // Contact Person (site contact from contact_people)
  contact_person_id?: number;
  contact_person_name?: string;

  // Financial
  currency: string;
  exchange_rate?: number;
  subtotal: number;
  tax_total: number;
  discount_amount?: number;
  discount_percentage?: number;
  grand_total: number;
  /** Rounding adjustment so subtotal + tax_total + rounding_adjustment = grand_total */
  rounding_adjustment?: number;

  // Payment tracking
  amount_paid?: number;
  balance_due?: number;
  payment_terms?: number;
  payment_status?: PaymentStatus;
  paid_at?: string;

  // Content
  notes?: string;
  terms_and_conditions?: string;
  internal_notes?: string;
  reference?: string;

  // Line items
  items?: DocumentLineItem[];

  // Metadata
  created_at?: string;
  updated_at?: string;
  created_by?: number;
  /** Resolved creator user (id, email, first_name, last_name) from the documents API. */
  creator?: { id?: number; email?: string; first_name?: string; last_name?: string } | null;
  custom_fields?: Record<string, unknown>;
  /** Cached AI suggestions from work_items.metadata->'ai_suggestions' (GL categorization, per-line suggestions). */
  ai_suggestions?: {
    categorization?: {
      account_code?: string | null;
      account_name?: string | null;
      confidence?: number;
      reasons?: string[];
      allocation_type?: string;
      inventory_item_match?: unknown;
    } | null;
    perLineCategorization?: Array<Record<string, unknown>> | null;
    generated_at?: string;
  } | null;
  /** Intake source column from work_items (e.g. 'email', 'whatsapp', 'internal', 'manual'). */
  source?: string | null;
  /** Vehicle linkage columns from work_items (real columns, not just custom_fields). */
  vehicle_id?: string | null;
  vehicle_registration?: string | null;

  // Sharing
  share_token?: string;
  public_link_enabled?: boolean;
  public_link_expires_at?: string;
  document_viewed?: boolean;
  first_viewed_at?: string;
  document_accepted?: boolean;
  accepted_at?: string;
  document_disputed?: boolean;
  disputed_at?: string;
  dispute_reason?: string;
  dispute_status?: string;

  // Collections workflow metadata
  collection_workflow_stage?: string;
  collection_next_follow_up_at?: string;
  collection_last_follow_up_at?: string;
  collection_promise_to_pay_date?: string;
  collection_note?: string;

  // Signatures
  document_signed?: boolean;
  signature_data?: string;
  signed_by_name?: string;
  signed_at?: string;

  // Relationships
  quote_id?: string;
  invoice_id_ref?: string;
  parent_id?: string;
  parent_document_id?: string;
  linked_document_id?: string;
  sales_order_id_ref?: string;
  purchase_order_id_ref?: string;

  /** Denormalised from invoice batch workflow (DB sync + batch APIs) */
  batch_id?: string | null;
  batch_number?: string | null;
  batch_status?: string | null;

  // Purchase-specific
  supplier_reference?: string;
  expected_delivery_date?: string;
  delivery_instructions?: string;

  // Sales-specific
  customer_po_number?: string;
  shipping_method?: string;
  shipping_cost?: number;

  // Fulfillment
  fulfillment_status?: string;
  quantity_received_percentage?: number;

  /** Parent job work item for billing_submission and DIN/expense documents */
  parent_job?: {
    id: string;
    item_number?: string | null;
    title?: string | null;
    contact_id?: number | null;
    contact_name?: string | null;
    /** Source appointment that linked the expense to this job */
    appointment_id?: string | null;
    appointment_contact_id?: number | null;
    appointment_contact_name?: string | null;
  } | null;

  /** Line items (returned by documents API) */
  line_items?: DocumentLineItem[];
}

export type SortField =
  | 'created_at'
  | 'updated_at'
  | 'issued_at'
  | 'due_date'
  | 'grand_total'
  | 'balance_due'
  | 'doc_number'
  | 'contact_name'
  | 'status'
  | 'payment_status';

export interface DocumentFilters {
  doc_type?: DocumentType | DocumentType[];
  status?: DocumentStatus | DocumentStatus[] | 'all';
  has_batch?: boolean;
  /** Invoices ready for batching: not draft/void and batch_id IS NULL */
  awaiting_statement_batch?: boolean;
  payment_status?: PaymentStatus | PaymentStatus[];
  document_disputed?: boolean;
  dispute_status?: string | string[];
  collection_workflow_stage?: string | string[];
  contact_id?: number;
  module_context?: string | string[];
  operational_item_types?: string | string[];
  operational_item_subtypes?: string | string[];
  search?: string;

  // Date filters
  issued_after?: string;
  issued_before?: string;
  due_after?: string;
  due_before?: string;
  created_after?: string;
  created_before?: string;

  // Amount filters
  amount_min?: number;
  amount_max?: number;

  // Reference filters
  customer_po_number?: string;
  supplier_reference?: string;

  // Pagination
  page?: number;
  limit?: number;

  // Sorting
  sort_by?: SortField;
  sort_order?: 'asc' | 'desc';
}

export interface Pagination {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface DashboardStats {
  document_counts: Record<DocumentType, number>;
  invoices: {
    total_invoiced: number;
    total_received: number;
    total_outstanding: number;
    unpaid_count: number;
    overdue_count: number;
  };
  expenses: {
    total_expenses: number;
    total_paid: number;
    total_payable: number;
  };
  quotes: {
    total_quotes: number;
    converted_quotes: number;
    conversion_rate: number;
  };
}

// ============================================================================
// HELPER: Safe error message extraction
// ============================================================================

function getErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  return 'An error occurred';
}

// ============================================================================
// HELPER: API call with auth
// ============================================================================

type ApiCallOptions = RequestInit & {
  suppressNotFound?: boolean;
};

async function apiCall<T>(
  endpoint: string,
  options: ApiCallOptions = {}
): Promise<T | null> {
  const { suppressNotFound = false, ...requestOptions } = options;
  const token = authService.getTokens()?.access_token;
  if (!token) {
    console.error('[useDocumentsWorkItems] No auth token');
    return null;
  }

  const response = await fetch(endpoint, {
    ...requestOptions,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...requestOptions.headers,
    },
  });

  const data = await response.json();

  if (suppressNotFound && response.status === 404) {
    return null;
  }

  if (!response.ok || !data.success) {
    console.error('[useDocumentsWorkItems] API error:', data.error, data.details || '');
    throw new Error(data.error || 'API request failed');
  }

  return data;
}

// ============================================================================
// HOOK
// ============================================================================

export function useDocumentsWorkItems(defaultDocType?: DocumentType) {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [currentDocument, setCurrentDocument] = useState<Document | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<Pagination>({
    total: 0,
    page: 1,
    pageSize: 25,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false
  });

  /**
   * Load documents with optional filters
   */
  const loadDocuments = useCallback(async (filters?: DocumentFilters) => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      const normalizedDocTypes = normalizeWorkItemDocumentTypes(filters?.doc_type);
      const normalizedDefaultDocTypes = normalizeWorkItemDocumentTypes(defaultDocType);

      // Handle doc_type filter
      if (filters?.doc_type) {
        if (normalizedDocTypes && normalizedDocTypes.length === 0) {
          setDocuments([]);
          setPagination({
            total: 0,
            page: 1,
            pageSize: 25,
            totalPages: 0,
            hasNextPage: false,
            hasPrevPage: false,
          });
          return;
        }

        appendQueryParam(params, 'doc_type', normalizedDocTypes);
      } else if (normalizedDefaultDocTypes && normalizedDefaultDocTypes.length > 0) {
        appendQueryParam(params, 'doc_type', normalizedDefaultDocTypes);
      }

      // Handle status filters
      if (filters?.status && filters.status !== 'all') {
        appendQueryParam(params, 'status', filters.status);
      }
      if (filters?.has_batch !== undefined) {
        appendQueryParam(params, 'has_batch', filters.has_batch);
      }
      if (filters?.awaiting_statement_batch !== undefined) {
        appendQueryParam(params, 'awaiting_statement_batch', filters.awaiting_statement_batch);
      }
      if (filters?.payment_status) {
        appendQueryParam(params, 'payment_status', filters.payment_status);
      }
      if (filters?.document_disputed !== undefined) {
        appendQueryParam(params, 'document_disputed', filters.document_disputed);
      }
      if (filters?.dispute_status) {
        appendQueryParam(params, 'dispute_status', filters.dispute_status);
      }
      if (filters?.collection_workflow_stage) {
        appendQueryParam(params, 'collection_workflow_stage', filters.collection_workflow_stage);
      }

      // Contact filter
      if (filters?.contact_id) {
        params.set('contact_id', String(filters.contact_id));
      }

      if (filters?.module_context) {
        appendQueryParam(params, 'module_context', filters.module_context);
      }
      if (filters?.operational_item_types) {
        appendQueryParam(params, 'operational_item_types', filters.operational_item_types);
      }
      if (filters?.operational_item_subtypes) {
        appendQueryParam(params, 'operational_item_subtypes', filters.operational_item_subtypes);
      }

      // Search
      if (filters?.search) {
        params.set('search', filters.search);
      }

      // Date filters
      if (filters?.issued_after) {
        params.set('issued_after', filters.issued_after);
      }
      if (filters?.issued_before) {
        params.set('issued_before', filters.issued_before);
      }
      if (filters?.due_after) {
        params.set('due_after', filters.due_after);
      }
      if (filters?.due_before) {
        params.set('due_before', filters.due_before);
      }
      if (filters?.created_after) {
        params.set('created_after', filters.created_after);
      }
      if (filters?.created_before) {
        params.set('created_before', filters.created_before);
      }

      // Amount filters
      if (filters?.amount_min !== undefined) {
        params.set('amount_min', String(filters.amount_min));
      }
      if (filters?.amount_max !== undefined) {
        params.set('amount_max', String(filters.amount_max));
      }

      // Reference filters
      if (filters?.customer_po_number) {
        params.set('customer_po_number', filters.customer_po_number);
      }
      if (filters?.supplier_reference) {
        params.set('supplier_reference', filters.supplier_reference);
      }

      // Pagination
      if (filters?.page) {
        params.set('page', String(filters.page));
      }
      if (filters?.limit) {
        params.set('limit', String(filters.limit));
      }

      // Sorting
      if (filters?.sort_by) {
        params.set('sort_by', filters.sort_by);
      }
      if (filters?.sort_order) {
        params.set('sort_order', filters.sort_order);
      }

      const result = await apiCall<{ data: Document[]; pagination: Pagination }>(
        `/api/v1/work-items/documents?${params.toString()}`
      );

      if (result) {
        setDocuments(result.data || []);
        if (result.pagination) {
          setPagination(result.pagination);
        }
      }

      return result;
    } catch (err: unknown) {
      setError(getErrorMessage(err));
      setDocuments([]);
      return null;
    } finally {
      setLoading(false);
    }
  }, [defaultDocType]);

  /**
   * Load a single document by ID (includes line items by default)
   */
  const loadDocument = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);

    try {
      const result = await apiCall<{ data: Document & { line_items?: DocumentLineItem[] } }>(
        `/api/v1/work-items/documents/${id}`,
        { suppressNotFound: true }
      );

      if (result?.data) {
        // Map line_items to items for compatibility.
        // When tax_inclusive=true, convert DB's NET unit_cost to GROSS
        // unit_price and DB's GROSS line_total to NET line_total, matching
        // the DocumentModal's convention (line_total is always NET, tax
        // added at document summary; unit_price is GROSS when tax_inclusive).
        const doc = {
          ...result.data,
          items: result.data.line_items?.map(item => {
            const isTaxInclusive = Boolean(item.tax_inclusive);
            const qty = Number(item.quantity) || 1;
            const dbLineTotal = Number(item.line_total ?? item.total_cost ?? 0);
            const dbTotalCost = Number(item.total_cost ?? 0);
            const taxAmount = Number(item.tax_amount ?? 0);
            let unitPrice: number;
            let lineTotal: number;
            if (isTaxInclusive) {
              unitPrice = qty > 0 ? Math.round((dbLineTotal / qty) * 100) / 100 : Number(item.unit_cost) || 0;
              lineTotal = dbTotalCost > 0 ? dbTotalCost : Math.round((dbLineTotal - taxAmount) * 100) / 100;
            } else {
              unitPrice = Number(item.unit_cost) || 0;
              lineTotal = dbTotalCost > 0 ? dbTotalCost : dbLineTotal;
            }
            return {
              ...item,
              expense_account_code: (item.expense_account_code ?? item.account_code) as string | undefined,
              unit_price: unitPrice,
              line_total: lineTotal,
            };
          })
        };
        setCurrentDocument(doc as Document);
        return doc as Document;
      }
      return null;
    } catch (err: unknown) {
      setError(getErrorMessage(err));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Load document line items by document ID
   * Returns items in the format expected by DocumentModal
   */
  const loadDocumentItems = useCallback(async (id: string) => {
    try {
      const result = await apiCall<{ data: Document & { line_items?: unknown[] } }>(
        `/api/v1/work-items/documents/${id}`,
        { suppressNotFound: true }
      );

      if (!result?.data) {
        return { data: [] };
      }

      type RawLine = Record<string, unknown>;
      const fromLineItems = result.data.line_items;
      const fromItems = (result.data as { items?: unknown }).items;
      const rawLines: RawLine[] | null = Array.isArray(fromLineItems)
        ? (fromLineItems as RawLine[])
        : Array.isArray(fromItems)
          ? (fromItems as RawLine[])
          : null;

      if (rawLines) {
        const n = (v: unknown): number => {
          const x = typeof v === 'number' ? v : Number(v);
          return Number.isFinite(x) ? x : 0;
        };
        // Map to expected format (API may expose `line_items` or legacy `items`)
        const items = rawLines.map((item) => {
          const isTaxInclusive = item.tax_inclusive === true;
          const qty = n(item.quantity) || 1;
          const dbLineTotal = n(item.line_total ?? item.total_cost);
          const dbTotalCost = n(item.total_cost);
          const taxAmount = n(item.tax_amount);

          // The DB stores:
          //   unit_cost  = NET (exclusive) unit cost
          //   total_cost = NET (exclusive) total
          //   line_total = GROSS (inclusive) total (NET + tax)
          //   tax_inclusive = whether the original prices included tax
          //
          // The DocumentModal frontend expects:
          //   unit_price = GROSS when tax_inclusive, NET when tax_exclusive
          //   line_total  = NET always (tax is added at the document summary)
          //
          // When tax_inclusive=true, derive the GROSS unit_price from the
          // GROSS line_total, and set line_total to the NET total_cost.
          let frontendUnitPrice: number;
          let frontendLineTotal: number;
          if (isTaxInclusive) {
            // GROSS unit price = GROSS line_total / quantity
            frontendUnitPrice = qty > 0 ? Math.round((dbLineTotal / qty) * 100) / 100 : n(item.unit_cost);
            // NET line total = total_cost (or line_total - tax_amount)
            frontendLineTotal = dbTotalCost > 0 ? dbTotalCost : Math.round((dbLineTotal - taxAmount) * 100) / 100;
          } else {
            frontendUnitPrice = n(item.unit_cost);
            // For tax_exclusive, line_total in DB is GROSS (NET + tax).
            // Frontend expects NET (tax added at summary).
            frontendLineTotal = dbTotalCost > 0 ? dbTotalCost : dbLineTotal;
          }

          return {
          id: item.id,
          document_id: id,
          item_id: item.item_id,
          item_code: item.item_code,
          item_name: item.item_name,
          description: item.description,
          notes: item.notes,
          quantity: item.quantity,
          unit: item.unit,
          unit_price: frontendUnitPrice,
          tax_rate_id: item.tax_rate_id,
          tax_rate: n(item.tax_rate),
          tax_amount: taxAmount,
          discount_amount: n(item.discount_amount),
          discount_percentage: n(item.discount_percentage),
          line_total: frontendLineTotal,
          sort_order: item.position,
          line_type: item.line_type,
          is_optional: item.is_optional === true,
          is_required: item.is_required === true,
          quantity_editable: item.quantity_editable !== false,
          price_editable: item.price_editable !== false,
          source_bundle_id: item.source_bundle_id,
          source_bundle_name: item.source_bundle_name,
          quantity_ordered: item.quantity_ordered,
          quantity_received: item.quantity_received,
          quantity_billed: item.quantity_billed,
          quantity_shipped: item.quantity_shipped,
          purchase_supplier_mapping_id: item.purchase_supplier_mapping_id == null ? undefined : n(item.purchase_supplier_mapping_id),
          purchase_supplier_contact_id: item.purchase_supplier_contact_id == null ? undefined : n(item.purchase_supplier_contact_id),
          purchase_supplier_name: typeof item.purchase_supplier_name === 'string' ? item.purchase_supplier_name : null,
          purchase_supplier_sku: typeof item.purchase_supplier_sku === 'string' ? item.purchase_supplier_sku : null,
          purchase_supplier_currency: typeof item.purchase_supplier_currency === 'string' ? item.purchase_supplier_currency : null,
          purchase_supplier_cost_price: item.purchase_supplier_cost_price == null ? null : n(item.purchase_supplier_cost_price),
          purchase_supplier_is_preferred: item.purchase_supplier_is_preferred === true,
          purchase_supplier_priority_rank: item.purchase_supplier_priority_rank == null ? null : n(item.purchase_supplier_priority_rank),
          purchase_supplier_lead_time_days: item.purchase_supplier_lead_time_days == null ? null : n(item.purchase_supplier_lead_time_days),
          purchase_supplier_selection_reason: typeof item.purchase_supplier_selection_reason === 'string' ? item.purchase_supplier_selection_reason : null,
          purchase_supplier_fallback_reason: typeof item.purchase_supplier_fallback_reason === 'string' ? item.purchase_supplier_fallback_reason : null,
          purchase_supplier_price_source: typeof item.purchase_supplier_price_source === 'string' ? item.purchase_supplier_price_source : null,
          purchase_supplier_matches_document_contact: item.purchase_supplier_matches_document_contact === true,
          expense_account_code: typeof (item.expense_account_code ?? item.account_code) === 'string' ? (item.expense_account_code ?? item.account_code) as string : undefined,
          allocation_type: typeof item.allocation_type === 'string' ? (item.allocation_type as 'job' | 'stock' | 'overhead' | 'equipment' | 'vehicle' | 'suspense') : undefined,
          linked_job_id: typeof item.linked_job_id === 'string' ? item.linked_job_id : undefined,
          linked_job_name: typeof item.linked_job_name === 'string' ? item.linked_job_name : undefined,
          linked_customer_id: typeof item.linked_customer_id === 'string' ? item.linked_customer_id : undefined,
          linked_customer_name: typeof item.linked_customer_name === 'string' ? item.linked_customer_name : undefined,
          is_billable: item.is_billable ?? undefined,
          billable_to_job_id: typeof item.billable_to_job_id === 'string' ? item.billable_to_job_id : undefined,
          markup_percentage: item.markup_percentage != null ? n(item.markup_percentage) : undefined,
          tax_inclusive: item.tax_inclusive != null ? Boolean(item.tax_inclusive) : undefined,
          };
        });
        return { data: items as DocumentLineItem[] };
      }
      return { data: [] };
    } catch (err: unknown) {
      console.error('[useDocumentsWorkItems] loadDocumentItems error:', err);
      return { data: [] };
    }
  }, []);

  /**
   * Generate a document number for a given type
   * The API auto-generates on create, but this is for preview
   */
  const generateDocumentNumber = useCallback(async (docType: DocumentType): Promise<string> => {
    const prefixes: Record<DocumentType, string> = {
      invoice: 'INV',
      quote: 'QUO',
      proposal: 'PROP',
      quote_request: 'QR',
      billing_submission: 'BLS',
      credit_note: 'CN',
      debit_note: 'DN',
      proforma: 'PRO',
      delivery_note: 'DN',
      waybill: 'WB',
      sales_order: 'SO',
      rma: 'RMA',
      purchase_order: 'PO',
      grn: 'GRN',
      supplier_credit: 'SC',
      supplier_quote: 'SQ',
      purchase_requisition: 'PR',
      rfq: 'RFQ',
      purchase_return: 'PRN',
      purchase: 'PUR',
      supplier_invoice: 'SINV',
      supplier_delivery_note: 'SDN',
      work_order: 'WO',
      expense: 'EXP',
      din: 'DIN',
      picking_slip: 'PICK',
      packing_slip: 'PACK',
      stock_adjustment: 'ADJ',
      transfer_order: 'TO',
      levy_cycle: 'LEV',
      meter_reading: 'MRD',
      utility_recovery: 'UTR',
      special_levy: 'SPL',
      scheme_budget: 'BUD',
      reserve_fund: 'RSV',
      deposit_holding: 'DEP',
      trust_transaction: 'TRX',
      payment_dispute: 'DSP',
      payment_plan: 'PPL',
      collection_case: 'CSE',
      invoice_followup: 'IFU',
      credit_request: 'CRQ',
      write_off: 'WOF',
    };

    const prefix = prefixes[docType] || 'DOC';
    const year = new Date().getFullYear().toString().slice(-2);
    const timestamp = Date.now().toString().slice(-4);

    // Return a placeholder - the actual number will be generated on save
    return `${prefix}-${year}-${timestamp}`;
  }, []);

  /**
   * Create a new document
   */
  const createDocument = useCallback(async (document: Partial<Document>) => {
    setLoading(true);
    setError(null);

    try {
      // Ensure doc_type is set
      if (!document.doc_type && defaultDocType) {
        document.doc_type = defaultDocType;
      }

      // Map items to API format (unit_price → unit_cost, items → line_items)
      const { items, ...docWithoutItems } = document as Partial<Document> & { items?: DocumentLineItem[] };
      // Strip null and empty-string values — backend Zod schema rejects
      // null for string fields and empty strings for UUID/enum fields
      const sanitized: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(docWithoutItems)) {
        if (value !== null && value !== '') {
          sanitized[key] = value;
        }
      }
      const apiDocument = {
        ...sanitized,
        line_items: items?.map((item: DocumentLineItem) => ({
          ...item,
          unit_cost: item.unit_price ?? item.unit_cost ?? 0,
          description: item.description || item.item_name || '',
          account_code: item.expense_account_code ?? item.account_code ?? null,
          unit: item.unit || item.unit_of_measure || 'unit',
        }))
      };

      const result = await apiCall<{ data: Document }>(
        '/api/v1/work-items/documents',
        {
          method: 'POST',
          body: JSON.stringify(apiDocument)
        }
      );

      if (result?.data) {
        // Reload documents to refresh the list
        await loadDocuments({ doc_type: document.doc_type });
        return result.data;
      }
      return null;
    } catch (err: unknown) {
      setError(getErrorMessage(err));
      return null;
    } finally {
      setLoading(false);
    }
  }, [defaultDocType, loadDocuments]);

  /**
   * Update an existing document
   */
  const updateDocument = useCallback(async (id: string, document: Partial<Document>) => {
    setLoading(true);
    setError(null);

    try {
      // Map items to API format (unit_price → unit_cost, items → line_items)
      const { items, ...docWithoutItems } = document as Partial<Document> & { items?: DocumentLineItem[] };
      // Strip null and empty-string values — backend Zod schema rejects
      // null for string fields and empty strings for UUID/enum fields
      const sanitized: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(docWithoutItems)) {
        if (value !== null && value !== '') {
          sanitized[key] = value;
        }
      }
      const apiDocument = {
        id,
        ...sanitized,
        line_items: items?.map((item: DocumentLineItem) => ({
          ...item,
          unit_cost: item.unit_price ?? item.unit_cost ?? 0,
          description: item.description || item.item_name || '',
          account_code: item.expense_account_code ?? item.account_code ?? null,
          unit: item.unit || item.unit_of_measure || 'unit',
        }))
      };

      const result = await apiCall<{ data: Document }>(
        '/api/v1/work-items/documents',
        {
          method: 'PUT',
          body: JSON.stringify(apiDocument)
        }
      );

      if (result?.data) {
        setCurrentDocument(result.data);
        await loadDocuments({ doc_type: result.data.doc_type });
        return result.data;
      }
      return null;
    } catch (err: unknown) {
      setError(getErrorMessage(err));
      return null;
    } finally {
      setLoading(false);
    }
  }, [loadDocuments]);

  /**
   * Delete a document
   */
  const deleteDocument = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);

    try {
      await apiCall<{ success: boolean }>(
        `/api/v1/work-items/documents?id=${id}`,
        { method: 'DELETE' }
      );

      // Remove from local state
      setDocuments(prev => prev.filter(d => d.id !== id));
      return true;
    } catch (err: unknown) {
      setError(getErrorMessage(err));
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Convert document to another type (e.g., quote → invoice)
   */
  const convertDocument = useCallback(async (id: string, targetType: DocumentType) => {
    setLoading(true);
    setError(null);

    try {
      const result = await apiCall<{ data: Document }>(
        '/api/v1/work-items/documents',
        {
          method: 'POST',
          body: JSON.stringify({ action: 'convertDocument', id, targetType })
        }
      );

      return result?.data || null;
    } catch (err: unknown) {
      setError(getErrorMessage(err));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Create a share link for a document
   */
  const createShareLink = useCallback(async (id: string, expiresInDays: number = 30) => {
    setLoading(true);
    setError(null);

    try {
      const result = await apiCall<{ data: { share_token: string; expires_at: string; share_url: string } }>(
        '/api/v1/work-items/documents',
        {
          method: 'POST',
          body: JSON.stringify({ action: 'createShareLink', id, expiresInDays })
        }
      );

      return result?.data || null;
    } catch (err: unknown) {
      setError(getErrorMessage(err));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Record a payment against an invoice or expense
   */
  const recordPayment = useCallback(async (
    id: string,
    amount: number,
    paymentDate?: string,
    paymentMethod?: string
  ) => {
    setLoading(true);
    setError(null);

    try {
      const result = await apiCall<{ data: { amount_paid: number; balance_due: number; payment_status: PaymentStatus } }>(
        '/api/v1/work-items/documents',
        {
          method: 'POST',
          body: JSON.stringify({
            action: 'recordPayment',
            id,
            amount,
            paymentDate: paymentDate || new Date().toISOString(),
            paymentMethod: paymentMethod || 'other'
          })
        }
      );

      return result?.data || null;
    } catch (err: unknown) {
      setError(getErrorMessage(err));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get dashboard statistics
   */
  const getDashboardStats = useCallback(async (): Promise<DashboardStats | null> => {
    setLoading(true);
    setError(null);

    try {
      const result = await apiCall<{ data: DashboardStats }>(
        '/api/v1/work-items/documents',
        {
          method: 'POST',
          body: JSON.stringify({ action: 'getDashboardStats' })
        }
      );

      return result?.data || null;
    } catch (err: unknown) {
      setError(getErrorMessage(err));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Calculate totals from line items (client-side calculation)
   * Returns: { subtotal, tax_total, discount_total, grand_total }
   */
  const calculateTotals = useCallback(async (items: DocumentLineItem[]) => {
    try {
      // Calculate totals client-side
      let subtotal = 0;
      let tax_total = 0;
      let discount_total = 0;

      for (const item of items) {
        // Parse all values as numbers to avoid string concatenation
        const quantity = parseFloat(String(item.quantity || 0)) || 0;
        const unitPrice = parseFloat(String(item.unit_price || 0)) || 0;
        const lineSubtotal = quantity * unitPrice;

        // Calculate line discount
        let lineDiscount = parseFloat(String(item.discount_amount || 0)) || 0;
        const discountPercentage = parseFloat(String(item.discount_percentage || 0)) || 0;
        if (discountPercentage > 0) {
          lineDiscount = lineSubtotal * (discountPercentage / 100);
        }

        const taxRate = parseFloat(String(item.tax_rate || 0)) || 0;
        const markupPct = parseFloat(String(item.markup_percentage || 0)) || 0;
        const baseAmount = lineSubtotal - lineDiscount;
        const markupAmount = markupPct > 0 ? baseAmount * (markupPct / 100) : 0;
        const taxableAmount = baseAmount + markupAmount;

        if (item.tax_inclusive && taxRate > 0) {
          // VAT-inclusive: unit_price already includes tax
          // Extract tax from the gross amount
          const lineTax = parseFloat(String(item.tax_amount || 0)) ||
            (taxableAmount - (taxableAmount / (1 + taxRate / 100)));
          const netAmount = taxableAmount - lineTax;
          subtotal += netAmount;
          discount_total += lineDiscount;
          tax_total += lineTax;
        } else {
          // VAT-exclusive: tax is added on top (standard behavior)
          const lineTax = parseFloat(String(item.tax_amount || 0)) || (taxableAmount * (taxRate / 100));
          subtotal += taxableAmount;
          discount_total += lineDiscount;
          tax_total += lineTax;
        }
      }

      const grand_total = subtotal - discount_total + tax_total;

      return {
        subtotal: Math.round(subtotal * 100) / 100,
        tax_total: Math.round(tax_total * 100) / 100,
        discount_total: Math.round(discount_total * 100) / 100,
        grand_total: Math.round(grand_total * 100) / 100
      };
    } catch (err: unknown) {
      console.error('[useDocumentsWorkItems] calculateTotals error:', err);
      return null;
    }
  }, []);

  /**
   * Update document status
   */
  const updateStatus = useCallback(async (id: string, status: DocumentStatus, docType?: DocumentType) => {
    setLoading(true);
    setError(null);

    try {
      const result = await apiCall<{ data: Document }>(
        `/api/v1/work-items/documents/${id}`,
        {
          method: 'PUT',
          body: JSON.stringify({ status })
        }
      );

      if (result?.data) {
        // Update local state
        setDocuments(prev => prev.map(d =>
          d.id === id ? { ...d, status } : d
        ));
        return result.data;
      }
      return null;
    } catch (err: unknown) {
      setError(getErrorMessage(err));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Send document via email (creates share link and updates status)
   */
  const sendDocument = useCallback(async (id: string, email?: string, docType?: DocumentType) => {
    setLoading(true);
    setError(null);

    try {
      // First create/regenerate share token
      const shareResult = await apiCall<{ data: { share_token: string } }>(
        `/api/v1/work-items/documents/${id}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ action: 'regenerate_share_token' })
        }
      );

      // Then update status to 'sent'
      await updateStatus(id, 'sent', docType);

      return { success: true, share_token: shareResult?.data?.share_token };
    } catch (err: unknown) {
      setError(getErrorMessage(err));
      return { success: false, error: getErrorMessage(err) };
    } finally {
      setLoading(false);
    }
  }, [updateStatus]);

  /**
   * Void a document
   */
  const voidDocument = useCallback(async (id: string, docType?: DocumentType) => {
    return updateStatus(id, 'void', docType);
  }, [updateStatus]);

  return {
    // State
    documents,
    currentDocument,
    loading,
    error,
    pagination,

    // Actions
    loadDocuments,
    loadDocument,
    loadDocumentItems,
    createDocument,
    updateDocument,
    deleteDocument,
    convertDocument,
    createShareLink,
    recordPayment,
    getDashboardStats,
    calculateTotals,
    generateDocumentNumber,
    updateStatus,
    sendDocument,
    voidDocument,

    // State setters (for manual updates)
    setDocuments,
    setCurrentDocument,
    setError,
  };
}

// ============================================================================
// BACKWARD COMPATIBLE WRAPPER HOOKS
// These provide the same interface as the old useInvoices, useQuotes, etc.
// ============================================================================

export function useInvoices() {
  const hook = useDocumentsWorkItems('invoice');
  return {
    invoices: hook.documents,
    currentInvoice: hook.currentDocument,
    loading: hook.loading,
    error: hook.error,
    pagination: hook.pagination,
    loadInvoices: hook.loadDocuments,
    loadInvoice: hook.loadDocument,
    createInvoice: hook.createDocument,
    updateInvoice: hook.updateDocument,
    deleteInvoice: hook.deleteDocument,
    recordPayment: hook.recordPayment,
    createShareLink: hook.createShareLink,
  };
}

export function useQuotes() {
  const hook = useDocumentsWorkItems('quote');
  return {
    quotes: hook.documents,
    currentQuote: hook.currentDocument,
    loading: hook.loading,
    error: hook.error,
    pagination: hook.pagination,
    loadQuotes: hook.loadDocuments,
    loadQuote: hook.loadDocument,
    createQuote: hook.createDocument,
    updateQuote: hook.updateDocument,
    deleteQuote: hook.deleteDocument,
    convertToInvoice: (id: string) => hook.convertDocument(id, 'invoice'),
    createShareLink: hook.createShareLink,
  };
}

export function useExpenses() {
  const hook = useDocumentsWorkItems('expense');
  return {
    expenses: hook.documents,
    currentExpense: hook.currentDocument,
    loading: hook.loading,
    error: hook.error,
    pagination: hook.pagination,
    loadExpenses: hook.loadDocuments,
    loadExpense: hook.loadDocument,
    createExpense: hook.createDocument,
    updateExpense: hook.updateDocument,
    deleteExpense: hook.deleteDocument,
    recordPayment: hook.recordPayment,
  };
}

export function usePurchaseOrders() {
  const hook = useDocumentsWorkItems('purchase_order');
  return {
    purchaseOrders: hook.documents,
    currentPurchaseOrder: hook.currentDocument,
    loading: hook.loading,
    error: hook.error,
    pagination: hook.pagination,
    loadPurchaseOrders: hook.loadDocuments,
    loadPurchaseOrder: hook.loadDocument,
    createPurchaseOrder: hook.createDocument,
    updatePurchaseOrder: hook.updateDocument,
    deletePurchaseOrder: hook.deleteDocument,
    convertToExpense: (id: string) => hook.convertDocument(id, 'expense'),
    convertToGRN: (id: string) => hook.convertDocument(id, 'grn'),
  };
}

export default useDocumentsWorkItems;
